/**
 * Blocks rendered markdown from loading subresources that carry our session
 * cookie.
 */

/**
 * Matched as a segment, not a prefix: artifact contents live under
 * `.../artifacts/.../files/...`, not only at `/files/...`.
 */
const OBJECT_STORE_SEGMENTS = ['files', 'local-files', 'minio'];

const INERT_SCHEMES = ['data:', 'blob:'];

function registrableDomain(hostname: string): string {
  const parts = hostname.split('.');
  return parts.length <= 2 ? hostname : parts.slice(-2).join('.');
}

function isSameSite(hostname: string, other: string): boolean {
  return (
    registrableDomain(hostname.toLowerCase()) ===
    registrableDomain(other.toLowerCase())
  );
}

function servesStoredObject(pathname: string): boolean {
  return pathname
    .split('/')
    .some(segment => OBJECT_STORE_SEGMENTS.includes(segment));
}

export type ResourceGuardContext = {
  /** Null outside a browser. */
  pageOrigin: string | null;
  credentialedHosts: string[];
};

function isCredentialed(
  resolved: URL,
  {pageOrigin, credentialedHosts}: ResourceGuardContext
): boolean {
  if (pageOrigin != null && resolved.origin === pageOrigin) {
    return true;
  }
  return credentialedHosts.some(host => isSameSite(resolved.hostname, host));
}

/** Unclassifiable input fails closed. */
export function isAllowedResourceUrl(
  rawUrl: string,
  context: ResourceGuardContext
): boolean {
  const url = rawUrl.trim();
  if (INERT_SCHEMES.some(scheme => url.toLowerCase().startsWith(scheme))) {
    return true;
  }

  let resolved: URL;
  try {
    // Relative resolves against the page; with no origin it throws.
    resolved = new URL(url, context.pageOrigin ?? undefined);
  } catch {
    return false;
  }
  if (resolved.protocol !== 'http:' && resolved.protocol !== 'https:') {
    return false;
  }
  if (!isCredentialed(resolved, context)) {
    return true;
  }
  return servesStoredObject(resolved.pathname);
}

/** Fetches a URL without an unambiguous `url()` token. */
const FETCHING_CSS_FUNCTIONS = /image-set|@import|expression\s*\(/i;
const CSS_URL = /url\(\s*(['"]?)([^'")]*)\1\s*\)/gi;

export function sanitizeStyleValue(
  styleValue: string,
  context: ResourceGuardContext
): string | null {
  if (FETCHING_CSS_FUNCTIONS.test(styleValue)) {
    return null;
  }
  const rewritten = styleValue.replace(CSS_URL, (match, _quote, target) =>
    isAllowedResourceUrl(target, context) ? match : 'none'
  );
  // A leftover `url`, or an escape that could spell one, is unaccountable.
  const residue = rewritten.replace(CSS_URL, '');
  if (/url/i.test(residue) || residue.includes('\\')) {
    return null;
  }
  return rewritten;
}

export function currentResourceGuardContext(): ResourceGuardContext {
  if (typeof window === 'undefined') {
    return {pageOrigin: null, credentialedHosts: []};
  }
  const pageOrigin = window.location?.origin ?? null;
  const backendHost = window.CONFIG?.BACKEND_HOST;
  const credentialedHosts: string[] = [];
  if (backendHost) {
    try {
      // Absolute (`https://api.wandb.ai`) or a path prefix (`/api/wandb`
      // under the Forge shell), so resolve rather than parse alone.
      credentialedHosts.push(
        new URL(backendHost, pageOrigin ?? undefined).hostname
      );
    } catch {
      // Malformed host leaves the page origin as the boundary.
    }
  }
  return {pageOrigin, credentialedHosts};
}
